/**
 * @module composables/useMediaPlayer
 *
 * ── FLOW / PURPOSE ─────────────────────────────────────────────────────
 *
 * Central singleton composable for the FYT playlist media player. The player
 * renders as a dedicated full-screen PAGE — the `/player` route (MediaPlayer.vue
 * is that route's component) — this is "playlist mode." Play actions set this
 * same shared state directly and call openPlayer(), which router.push()es to
 * /player; because it's the same tab/JS context the state is already there, so
 * there's nothing to hand off. `showPlayer` is a computed derived from the
 * route (name === "player"). (The separate single-episode "Play Episode"
 * quick-play — useEpisodePlayer.js / EpisodePlayerDialog.vue — is "episode
 * mode": an in-page dialog + Picture-in-Picture, and does NOT use this route.)
 *
 * THE PLAYLIST IS THE ONLY PLAY QUEUE. There is exactly one playback mode:
 * sequential playback through the playlist episodes array. Every "play"
 * action goes through the queue:
 *   - queueAndPlay ("Play now" from Latest/Episodes/Search/History/menus):
 *     adds the episode to the top of the playlist and plays it there.
 *   - "Play All" / "Play from here" / playEpisode: play the queue from a
 *     given position.
 *   - Single-episode listening is expressed with the End-of-Episode sleep
 *     timer, not a separate mode.
 * When the auto-remove setting is on, finished episodes leave the queue
 * (finishAndAdvance) — like a podcast app's up-next queue.
 *
 * Key capabilities:
 *   - Playback state (play/pause, seek, volume, playback rate)
 *   - Sleep timer (timed countdown OR "end of episode" mode)
 *   - Skip intro / skip outro (configurable durations, per-episode flags)
 *   - Per-episode position persistence (resume where you left off)
 *   - Playlist CRUD helpers (fetch, add, remove episodes)
 *   - User preference persistence to localStorage
 *
 * Implemented as a **singleton** via the module-level mediaPlayerInstance
 * variable: every component that calls useMediaPlayer() gets the exact
 * same reactive state. This is critical because the player page (the /player
 * route's component) and trigger buttons scattered across views (PlaylistView,
 * SearchView, HistoryView, EpisodeContextMenu) must share one unified state —
 * and it's what lets a play action set the queue and then simply navigate to
 * /player with the state already in place.
 *
 * ── localStorage KEYS ──────────────────────────────────────────────────
 *   mediaPlayerEpisodePositions  – JSON map of episodeId -> seconds
 *   mediaPlayerSleepTimer        – integer (-1 | 0 | minutes)
 *   mediaPlayerVolume            – float 0-1
 *   mediaPlayerPlaybackRate      – float (e.g. 1, 1.5, 2)
 *   mediaPlayerSkipIntro         – integer seconds
 *   mediaPlayerSkipOutro         – integer seconds
 *   mediaPlayerSkipIntroEnabled  – "true" | "false"
 *   mediaPlayerSkipOutroEnabled  – "true" | "false"
 *   mediaPlayerAutoRemovePlayed  – "true" | "false" (finished eps leave the queue)
 *   mediaPlayerMobileMode        – legacy key, cleared on logout
 * ───────────────────────────────────────────────────────────────────────
 */

// Vue reactivity primitives: ref for mutable state, computed for derived
// values, watch for side-effects when state changes
import { ref, computed, watch } from "vue";

// Axios-based API client with JWT auth interceptors (see api/config.js)
import { api } from "../api/config.js";
import { triggerCache } from "../api/index.js";

// The router singleton — the playlist player is a dedicated /player ROUTE, so
// openPlayer/closePlayer navigate rather than toggling a dialog flag. Imported
// as the default export (not useRouter()) because this composable is a
// module-scoped singleton created outside any component setup. The
// router → PlaylistView → usePlaylist → useMediaPlayer → router import cycle is
// safe: `router` is only used at runtime (inside openPlayer / the showPlayer
// computed), never at module-eval time.
import router from "@/router";

// useAppMenu provides the global snackbar that appears in App.vue
// We import it here to show caching progress notifications to the user
import { useAppMenu } from "./useAppMenu.js";

/**
 * Singleton instance holder. Set on first call to useMediaPlayer()
 * and returned on all subsequent calls.
 * @type {Object|null}
 */
let mediaPlayerInstance = null;

/**
 * Create or return the singleton media player composable.
 *
 * @returns {Object} The shared media player state and methods
 */
export function useMediaPlayer() {
  // Return existing singleton instance if already created
  if (mediaPlayerInstance) {
    return mediaPlayerInstance;
  }

  // ── Reactive state ──────────────────────────────────────────────

  /** Playlist episodes array (used in playlist/sequential playback mode) */
  const episodes = ref([]);

  /** Index of the currently playing episode within the episodes array */
  const currentEpisodeIndex = ref(0);

  /** Current playback position in seconds */
  const currentTime = ref(0);

  /** Total duration of the current episode in seconds */
  const duration = ref(0);

  /** Whether playback is active (true) or paused (false) */
  const isPlaying = ref(false);

  /** Audio volume level from 0 (muted) to 1 (full) */
  const volume = ref(1);

  /** Playback speed multiplier (e.g., 1 = normal, 1.5 = 1.5x speed) */
  const playbackRate = ref(1);

  /** Timeout ID for the sleep timer's setTimeout call */
  const sleepTimer = ref(null);

  /**
   * Sleep timer duration setting:
   * -1 = "End of Episode" mode (stop after current episode ends)
   *  0 = timer disabled (default)
   * >0 = number of minutes until auto-stop
   */
  const sleepTimerDuration = ref(-1);

  /** Whether the sleep timer is currently counting down */
  const sleepTimerActive = ref(false);

  /** Absolute timestamp (Date.now()) when the sleep timer will fire */
  const sleepTimerEndTime = ref(null);

  /** Remaining seconds on the countdown timer (updated every second) */
  const sleepTimerRemainingSeconds = ref(0);

  /** Number of seconds to skip at the start of each episode */
  const skipIntroDuration = ref(0);

  /** Number of seconds before the end to trigger skip-to-next */
  const skipOutroDuration = ref(0);

  /** Whether the skip-intro feature is enabled (toggle in settings) */
  const skipIntroEnabled = ref(true);

  /** Whether the skip-outro feature is enabled (toggle in settings) */
  const skipOutroEnabled = ref(true);

  /**
   * Whether a finished episode is automatically removed from the playlist
   * (the playlist is the play QUEUE — like Overcast/Pocket Casts queues,
   * what you've listened to leaves the queue). Toggle in Player Settings.
   */
  const autoRemovePlayed = ref(true);

  /** Flag to prevent re-triggering intro skip on the same episode */
  const introSkipped = ref(false);

  /** Flag to prevent re-triggering outro skip on the same episode */
  const outroSkipped = ref(false);

  /**
   * Whether the playlist player is showing — DERIVED from the route, since the
   * player is now the dedicated /player page. Kept named `showPlayer` because
   * App.vue's chrome-hiding guards and MediaPlayer.vue's watchers already read
   * it; as a computed it also auto-flips false on browser back/gesture (the
   * route leaves /player), which is why the old manual pushState/popstate
   * bookkeeping could be deleted.
   */
  const showPlayer = computed(() => router.currentRoute.value.name === "player");

  /** Whether a playlist fetch or episode load is in progress */
  const loading = ref(false);

  /** Total number of episodes in the user's playlist */
  const total_playlist = ref(0);

  /** Snackbar notification state for media player messages */
  const snackbar = ref({
    show: false,
    message: "",
    color: "info",
  });

  /**
   * Map of episodeId -> playback position (seconds).
   * Tracks where the user left off in each episode so playback
   * can resume from the saved position.
   */
  const episodePositions = ref({});

  // NOTE: there is no "standalone" playback mode. The playlist is the only
  // play queue — every play action goes through it (see queueAndPlay).
  // When an uncached episode is played, playAllFromIndex triggers the server
  // cache and relies on list polling to update progress via useAppMenu state
  // (cachingEpisodeId, cachingProgress, cachingStatusMessage), then plays.

  // ── Computed properties ─────────────────────────────────────────

  /**
   * The currently playing episode object — the episode at
   * currentEpisodeIndex in the playlist queue.
   */
  const currentEpisode = computed(() => {
    return episodes.value[currentEpisodeIndex.value] || null;
  });

  /**
   * Playback progress as a percentage (0-100).
   * Returns 0 if duration is unknown or zero.
   */
  const progress = computed(() => {
    return duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0;
  });

  /**
   * Formatted countdown string for the sleep timer (e.g., "14:32").
   * Returns empty string when no countdown is active.
   */
  const sleepTimerFormatted = computed(() => {
    const remaining = sleepTimerRemainingSeconds.value;
    if (remaining <= 0) return "";

    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  });

  /**
   * Whether intro skip should be applied: feature is enabled,
   * a duration is configured, and it hasn't been skipped yet
   * for the current episode.
   */
  const shouldSkipIntro = computed(() => {
    return skipIntroEnabled.value && skipIntroDuration.value > 0 && !introSkipped.value;
  });

  /**
   * Whether outro skip should be applied: feature is enabled,
   * a duration is configured, it hasn't fired yet, and we
   * know the total duration of the episode.
   */
  const shouldSkipOutro = computed(() => {
    return (
      skipOutroEnabled.value && skipOutroDuration.value > 0 && !outroSkipped.value && duration.value > 0
    );
  });

  /** Whether there is a next episode available in the playlist */
  const hasNextEpisode = computed(() => {
    return currentEpisodeIndex.value < episodes.value.length - 1;
  });

  /** Whether there is a previous episode available in the playlist */
  const hasPreviousEpisode = computed(() => {
    return currentEpisodeIndex.value > 0;
  });

  /** Position indicator text (e.g., "2 of 12") for the playlist queue. */
  const playlistPositionText = computed(() => {
    if (episodes.value.length === 0) return '';
    return `${currentEpisodeIndex.value + 1} of ${episodes.value.length}`;
  });

  // ── localStorage persistence ────────────────────────────────────

  /**
   * Load persisted episode positions and user preferences from localStorage.
   * Called once during singleton initialization.
   */
  const loadFromStorage = () => {
    try {
      // Restore per-episode playback positions
      const savedPositions = localStorage.getItem("mediaPlayerEpisodePositions");
      if (savedPositions) {
        episodePositions.value = JSON.parse(savedPositions);
      }

      // Restore user preferences (each stored as a separate key)
      const savedSleepTimer = localStorage.getItem("mediaPlayerSleepTimer");
      const savedVolume = localStorage.getItem("mediaPlayerVolume");
      const savedPlaybackRate = localStorage.getItem("mediaPlayerPlaybackRate");
      const savedSkipIntro = localStorage.getItem("mediaPlayerSkipIntro");
      const savedSkipOutro = localStorage.getItem("mediaPlayerSkipOutro");
      const savedSkipIntroEnabled = localStorage.getItem("mediaPlayerSkipIntroEnabled");
      const savedSkipOutroEnabled = localStorage.getItem("mediaPlayerSkipOutroEnabled");
      const savedAutoRemovePlayed = localStorage.getItem("mediaPlayerAutoRemovePlayed");

      if (savedSkipIntro) skipIntroDuration.value = parseInt(savedSkipIntro);
      if (savedSkipOutro) skipOutroDuration.value = parseInt(savedSkipOutro);
      if (savedSkipIntroEnabled !== null) skipIntroEnabled.value = savedSkipIntroEnabled === 'true';
      if (savedSkipOutroEnabled !== null) skipOutroEnabled.value = savedSkipOutroEnabled === 'true';
      if (savedAutoRemovePlayed !== null) autoRemovePlayed.value = savedAutoRemovePlayed === 'true';
      if (savedSleepTimer) sleepTimerDuration.value = parseInt(savedSleepTimer);
      if (savedVolume) volume.value = parseFloat(savedVolume);
      if (savedPlaybackRate) playbackRate.value = parseFloat(savedPlaybackRate);
    } catch (error) {
      console.error("Error loading from storage:", error);
    }
  };

  /**
   * Save episode positions and user preferences to localStorage.
   * Called on every position update (debounced) and preference change.
   */
  const saveToStorage = () => {
    try {
      // Save the per-episode playback positions map
      localStorage.setItem(
        "mediaPlayerEpisodePositions",
        JSON.stringify(episodePositions.value)
      );

      // Save each user preference individually
      localStorage.setItem(
        "mediaPlayerSkipIntro",
        skipIntroDuration.value.toString(),
      );
      localStorage.setItem(
        "mediaPlayerSkipOutro",
        skipOutroDuration.value.toString(),
      );
      localStorage.setItem(
        "mediaPlayerSkipIntroEnabled",
        skipIntroEnabled.value.toString(),
      );
      localStorage.setItem(
        "mediaPlayerSkipOutroEnabled",
        skipOutroEnabled.value.toString(),
      );
      localStorage.setItem(
        "mediaPlayerAutoRemovePlayed",
        autoRemovePlayed.value.toString(),
      );
      localStorage.setItem(
        "mediaPlayerSleepTimer",
        sleepTimerDuration.value.toString(),
      );
      localStorage.setItem("mediaPlayerVolume", volume.value.toString());
      localStorage.setItem(
        "mediaPlayerPlaybackRate",
        playbackRate.value.toString(),
      );
    } catch (error) {
      console.error("Error saving to storage:", error);
    }
  };

  // Load persisted data on singleton initialization
  loadFromStorage();

  // ── Watchers for auto-persistence ───────────────────────────────

  /**
   * Debounced watcher on currentTime: updates the episode position map
   * in memory on every time change, but only writes to localStorage
   * every 1 second to avoid excessive I/O.
   */
  let savePositionsTimeout = null;
  watch(currentTime, (newTime) => {
    if (currentEpisode.value) {
      // Update in-memory position immediately
      episodePositions.value[currentEpisode.value.id] = newTime;

      // Debounce the localStorage write to once per second
      clearTimeout(savePositionsTimeout);
      savePositionsTimeout = setTimeout(() => {
        saveToStorage();
      }, 1000);
    }
  }, { immediate: true });

  /**
   * Auto-activate sleep timer for End of Episode mode.
   * EoE is stateless (no countdown), so it's safe to activate from saved
   * preference or default without user interaction.
   */
  watch(sleepTimerDuration, (duration) => {
    if (duration === -1) {
      sleepTimerActive.value = true;
    }
  }, { immediate: true });

  /**
   * Watch all user preference refs and save to localStorage whenever
   * any of them change.
   */
  watch(
    [
      sleepTimerDuration,
      volume,
      playbackRate,
      skipIntroDuration,
      skipOutroDuration,
      skipIntroEnabled,
      skipOutroEnabled,
      autoRemovePlayed,
    ],
    () => {
      saveToStorage();
    },
  );

  // ── Episode position methods ────────────────────────────────────

  /**
   * Get the saved playback position for an episode.
   *
   * @param {number|string} episodeId - The episode's unique ID
   * @returns {number} Saved position in seconds, or 0 if none saved
   */
  const getEpisodePosition = (episodeId) => {
    return episodePositions.value[episodeId] || 0;
  };

  /**
   * Get the playback position for an episode, applying intro skip
   * if the saved position is earlier than the intro duration.
   * This ensures episodes always start past the intro when skip is configured.
   *
   * @param {number|string} episodeId - The episode's unique ID
   * @returns {number} Position in seconds to start playback from
   */
  const getEpisodePositionWithIntroSkip = (episodeId) => {
    const savedPosition = episodePositions.value[episodeId] || 0;

    // If the saved position is before the intro skip point,
    // start at the intro skip point instead
    if (skipIntroDuration.value > 0 && savedPosition < skipIntroDuration.value) {
      return skipIntroDuration.value;
    }

    return savedPosition;
  };

  // ── Skip intro/outro methods ────────────────────────────────────

  /**
   * Attempt to skip the intro of the current episode.
   * Only skips if the intro duration is configured and the current
   * playback position is still within the intro region.
   *
   * @returns {boolean} True if the skip was performed
   */
  const skipIntro = () => {
    if (
      skipIntroDuration.value > 0 &&
      currentTime.value < skipIntroDuration.value
    ) {
      const newTime = Math.max(skipIntroDuration.value, currentTime.value);
      seek(newTime);
      introSkipped.value = true;
      return true;
    }
    return false;
  };

  /**
   * Check if the outro region has been reached and skip if so.
   * An outro skip counts as FINISHING the episode: it routes through
   * finishAndAdvance, so the episode's position is reset and (when
   * autoRemovePlayed is on) it leaves the queue, then playback advances
   * to the next episode — unless the End-of-Episode sleep timer stops it.
   *
   * @returns {boolean} True if the outro skip was triggered
   */
  const skipOutro = () => {
    if (skipOutroDuration.value > 0 && duration.value > 0) {
      // Calculate when the outro begins (duration minus skip seconds)
      const outroStartTime = duration.value - skipOutroDuration.value;
      if (currentTime.value >= outroStartTime && !outroSkipped.value) {
        outroSkipped.value = true;

        // Sleep timer (End of Episode) stops playback here; still run the
        // finish handling (position reset + queue removal), just don't advance.
        const stoppedByTimer = checkSleepTimerOnEnd();
        finishAndAdvance({ advance: !stoppedByTimer });
        return true;
      }
    }
    return false;
  };

  /**
   * Reset the intro/outro skip flags. Called when switching to a
   * new episode so the skips can fire again.
   */
  const resetSkipFlags = () => {
    introSkipped.value = false;
    outroSkipped.value = false;
  };

  // ── Sleep timer methods ─────────────────────────────────────────

  /**
   * Update the countdown display. Called every second by the
   * countdown interval. Stops playback when the timer reaches zero.
   */
  const updateCountdown = () => {
    if (sleepTimerActive.value && sleepTimerEndTime.value) {
      const now = Date.now();
      const remaining = sleepTimerEndTime.value - now;

      if (remaining <= 0) {
        // Timer expired -- stop playback
        stopPlaybackDueToSleepTimer();
      } else {
        // Update the display with remaining seconds
        sleepTimerRemainingSeconds.value = Math.floor(remaining / 1000);
      }
    }
  };

  /** Interval ID for the 1-second countdown updates */
  let countdownInterval = null;

  /**
   * Start the 1-second countdown interval for the sleep timer display.
   * Clears any existing interval first to prevent duplicates.
   */
  const startCountdown = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
    }
    countdownInterval = setInterval(updateCountdown, 1000);
    updateCountdown(); // Immediate first tick
  };

  /**
   * Stop the countdown interval and reset the remaining seconds display.
   */
  const stopCountdown = () => {
    if (countdownInterval) {
      clearInterval(countdownInterval);
      countdownInterval = null;
    }
    sleepTimerRemainingSeconds.value = 0;
  };

  /**
   * Set the sleep timer to a specific duration.
   *
   * @param {number} minutes - Timer duration:
   *   -1 = "End of Episode" (stop when current episode ends)
   *    0 = disable timer
   *   >0 = stop playback after this many minutes
   */
  const setSleepTimer = (minutes) => {
    sleepTimerDuration.value = minutes;
    // Clear any existing timer before setting a new one
    clearSleepTimer();

    if (minutes === -1) {
      // "End of Episode" mode: no countdown, just a flag
      sleepTimerActive.value = true;
      sleepTimerEndTime.value = null;
      sleepTimerRemainingSeconds.value = 0;
    } else if (minutes > 0) {
      // Timed mode: set up both the countdown display and the actual timeout
      sleepTimerActive.value = true;
      sleepTimerEndTime.value = Date.now() + minutes * 60 * 1000;
      sleepTimerRemainingSeconds.value = minutes * 60;
      startCountdown();

      // Set the actual timeout that will stop playback
      sleepTimer.value = setTimeout(
        () => {
          stopPlaybackDueToSleepTimer();
        },
        minutes * 60 * 1000,
      );
    } else {
      // Disabled (minutes === 0): clear everything
      sleepTimerActive.value = false;
      sleepTimerEndTime.value = null;
      sleepTimerRemainingSeconds.value = 0;
      stopCountdown();
    }
  };

  /**
   * Stop playback because the sleep timer has expired.
   * Pauses the audio/video, clears all timer state, and
   * stops the countdown display.
   */
  const stopPlaybackDueToSleepTimer = () => {
    isPlaying.value = false;
    sleepTimerActive.value = false;
    sleepTimerEndTime.value = null;
    sleepTimerRemainingSeconds.value = 0;
    if (sleepTimer.value) {
      clearTimeout(sleepTimer.value);
      sleepTimer.value = null;
    }
    stopCountdown();
  };

  /**
   * Clear the sleep timer without stopping playback.
   * Used when changing timer settings or resetting.
   */
  const clearSleepTimer = () => {
    if (sleepTimer.value) {
      clearTimeout(sleepTimer.value);
      sleepTimer.value = null;
    }
    sleepTimerActive.value = false;
    sleepTimerEndTime.value = null;
    sleepTimerRemainingSeconds.value = 0;
    stopCountdown();
  };

  /**
   * Check if the "End of Episode" sleep timer mode should stop playback.
   * Called when an episode naturally ends. If the timer is in EoE mode
   * (-1) and active, it stops playback.
   *
   * @returns {boolean} True if playback was stopped by the timer
   */
  const checkSleepTimerOnEnd = () => {
    // End of Episode mode is active whenever the duration is -1.
    // No separate activation needed — it's a persistent preference.
    if (sleepTimerDuration.value === -1) {
      stopPlaybackDueToSleepTimer();
      return true;
    }
    return false;
  };

  // ── Snackbar helper ─────────────────────────────────────────────

  /**
   * Show a snackbar notification in the media player.
   *
   * @param {string} message - The message to display
   * @param {string} [color="info"] - Snackbar color ("info", "error", "success")
   */
  const showSnackbar = (message, color = "info") => {
    snackbar.value = { show: true, message, color };
  };

  // Get the global app snackbar from useAppMenu to show caching progress
  // This snackbar is connected to the v-snackbar in App.vue
  const { snackbar: appSnackbar, cachingEpisodeId, cachingProgress, cachingStatusMessage, cachingEpisodeTitle, cachingProgressMessage, cachingError } = useAppMenu();

  // ── Playlist API methods ────────────────────────────────────────

  /**
   * Fetch the user's playlist from the server.
   * Updates the episodes array and total count.
   */
  const getPlaylist = async () => {
    loading.value = true;
    try {
      const { data } = await api.get("/playlist");
      if (!data.success) {
        throw new Error(data.error.message);
      }
      episodes.value = data.playlist;
      // Total count comes from the first episode's metadata
      total_playlist.value = data.playlist[0]?.total_episodes || 0;
      loading.value = false;
    } catch (error) {
      loading.value = false;
      console.error("Error loading playlist:", error);
    }
  };

  /**
   * Remove an episode from the playlist via the API.
   * On success, removes it from the local episodes array for
   * immediate UI update and shows a success notification.
   *
   * @param {Object} episode - The episode to remove
   * @param {number} episode.id - The episode's database ID
   * @param {string} episode.title - The episode title (for notification)
   */
  const removeEpisode = async (episode) => {
    try {
      const id = episode.id;
      const { data } = await api.delete(`/playlist/episode/${id}`);

      // Remove from local array for immediate UI update
      const existingIndex = episodes.value.findIndex(
        (episode) => episode.id === id,
      );
      if (existingIndex >= 0) {
        episodes.value.splice(existingIndex, 1);
      }

      showSnackbar(
        `Episode "${episode.title}" successfully removed from playlist`,
      );
    } catch (error) {
      showSnackbar(
        `Error removing episode "${episode.title}" from playlist: ${error}`,
        "error",
      );
      console.error("Error removing from playlist:", error);
    }
  };

  /**
   * Append new episodes to the existing playlist array.
   *
   * @param {Array} newEpisodes - Episodes to append
   */
  const addEpisodes = (newEpisodes) => {
    episodes.value = [...episodes.value, ...newEpisodes];
  };

  /**
   * Replace the entire episodes array with a new set.
   *
   * @param {Array} newEpisodes - The new episodes array
   */
  const setEpisodes = (newEpisodes) => {
    episodes.value = newEpisodes;
  };

  // ── Playback control methods ────────────────────────────────────

  /**
   * Play an episode from the current playlist by its ID.
   * Finds the episode in the playlist, sets the index, restores
   * any saved position (with intro skip), builds the media URL,
   * and starts playback.
   *
   * @param {number|string} episodeId - The ID of the episode to play
   */
  const playEpisode = (episodeId) => {
    const index = episodes.value.findIndex((ep) => ep.id === episodeId);
    if (index !== -1) {
      // Flush the current episode's position to localStorage before switching,
      // ensuring no progress is lost on the episode being left behind.
      saveToStorage();

      // Set the playlist index to the found episode
      currentEpisodeIndex.value = index;

      // Restore saved position (or skip to intro end if applicable).
      // Save directly into episodePositions to guarantee the position is
      // present when onLoadedMetadata fires — the currentTime watcher is a
      // pre-flush watcher and may not execute before the currentEpisode
      // watcher's async resolveVideoUrl completes and loads the video.
      const savedPosition = getEpisodePositionWithIntroSkip(episodeId);
      episodePositions.value[episodeId] = savedPosition;
      currentTime.value = savedPosition;

      // Reset skip flags since this is a new episode context
      resetSkipFlags();

      // Setting isPlaying triggers the MediaPlayer component to auto-play
      isPlaying.value = true;

      // Build the media URL based on episode type (audio/video)
      const mode = "play";
      if (currentEpisode.value) {
        currentEpisode.value.video_url =
          currentEpisode.value.episode_type === "audio"
            ? `${import.meta.env.VITE_API_BASE_URL}/${mode}/${currentEpisode.value.video_id}.mp3`
            : `${import.meta.env.VITE_API_BASE_URL}/${mode}/${currentEpisode.value.video_id}.mp4`;
      }

      // Open the media player dialog immediately
      openPlayer();
    } else {
      console.error("Episode not found:", episodeId);
    }
  };

  /**
   * queueAndPlay - THE unified "Play now" entry point. The playlist is the
   * only play queue: playing an episode from any list (Latest, Episodes,
   * Search, History, context menu) means adding it to the top of the
   * playlist and starting playlist playback from there. There is no
   * standalone playback mode.
   *
   * Flow:
   * 1. Add episode to playlist via API (server inserts at top, position 0;
   *    409 "already in playlist" is treated as success — plays IN PLACE at
   *    its existing position, no reorder surprise)
   * 2. Fetch the updated playlist and locate the episode
   * 3. Turn the sleep timer off (playing from the queue = binge semantics;
   *    a single-episode stop is available via the End-of-Episode timer)
   * 4. playAllFromIndex from that position — its uncached path triggers the
   *    server cache, shows the preparing overlay, polls /playlist/status,
   *    and auto-plays when the file is ready
   *
   * @param {Object} episode - The episode to play (id, video_id, episode_type, ...)
   */
  const queueAndPlay = async (episode) => {
    // Open the dialog immediately (before the awaits below) so the user sees
    // it appear the instant they tap Play, rather than after a network
    // round-trip. Whatever was playing previously (if anything) keeps showing
    // behind it very briefly — playAllFromIndex replaces it a moment later,
    // either with real playback (cached) or the preparing overlay (uncached).
    openPlayer();

    // 1. Check if episode is already in playlist locally to avoid unnecessary 409
    const alreadyInPlaylist = episodes.value.some((ep) => ep.id === episode.id);

    // 2. Add to playlist via API (only if not already there)
    if (!alreadyInPlaylist) {
      try {
        await api.put("/playlist/episode/" + episode.id);
      } catch (err) {
        // 409 = already in playlist, which is OK — race condition with another client
        if (err.response?.status !== 409) throw err;
      }
    }

    // 3. Fetch updated playlist to find episode's position (new episodes are at top)
    const { data } = await api.get("/playlist");
    const playlist = data.playlist || [];
    const index = playlist.findIndex((ep) => ep.id === episode.id);

    // Episode missing even after a successful add — something is genuinely
    // wrong server-side. Surface it instead of silently doing nothing.
    if (index === -1) {
      showSnackbar(
        `Could not queue "${episode.title || episode.episode_title || "episode"}" for playback`,
        "error",
      );
      return;
    }

    // 4. Binge semantics: playing from the queue continues down the queue.
    //    (No separate cache-fire needed here — playAllFromIndex's uncached
    //    path triggers the server cache and polls until playable.)
    setSleepTimer(0);
    playAllFromIndex(playlist, index);
  };


  /**
   * cacheEpisodeWithProgress - Caches an episode with progress feedback.
   *
   * Triggers caching and shows snackbar. Relies on list view polling to update
   * progress in useAppMenu state. Shows completion snackbar after list polling
   * would have detected the download finishing.
   *
   * @param {Object} episodeData - The episode data to cache
   */
  const cacheEpisodeWithProgress = async (episodeData) => {
    const isActivelyDownloading = episodeData.cached_at === "downloading" ||
      episodeData.progress_message === "downloading";
    const isCached = episodeData.cached_at && episodeData.cached_at !== "downloading";

    if (isCached || isActivelyDownloading) {
      appSnackbar.value = { show: true, message: "Episode already caching or cached", color: "info" };
      return;
    }

    // Set caching state in useAppMenu
    const title = episodeData.title || episodeData.episode_title || "Episode";
    appSnackbar.value = { show: true, message: `Caching "${title}"…`, color: "info" };
    cachingEpisodeId.value = episodeData.id;
    cachingEpisodeTitle.value = title;
    cachingProgress.value = 0;
    cachingStatusMessage.value = `Caching: ${title}...`;

    // Trigger caching. triggerCache normalizes a give-up response (e.g. the
    // episode already hit maxDownloadFailures) — a 200 OK with
    // success:false, so a plain .catch() never saw it — into a checkable
    // result. Without this, list polling would just never see cached_at
    // change, and this caching-state UI would sit there indefinitely with
    // no explanation. Confirmed live on episode 262742.
    triggerCache(episodeData).then((result) => {
      if (!result.ok) {
        cachingEpisodeId.value = null;
        cachingProgress.value = 0;
        cachingStatusMessage.value = "";
        appSnackbar.value = { show: true, message: `"${title}": ${result.message}`, color: "error" };
      }
      // On success, list polling will detect when caching completes and
      // clear cachingEpisodeId. The initial snackbar above auto-closes
      // after 3 seconds (timeout="3000" in App.vue).
    });
  };

  /**
   * Open the playlist player — now a dedicated /player PAGE, so this simply
   * navigates there. Idempotent: playback methods call it liberally (e.g.
   * queueAndPlay calls it up front, then playAllFromIndex/playEpisodeNow call
   * it again once the real episode resolves; finishAndAdvance calls it on
   * auto-advance) — a push while already on /player is skipped so we don't
   * stack duplicate history entries. router.push() creates the history entry
   * itself, so browser back/gesture returns to the list the user came from
   * (no manual pushState/popstate bookkeeping needed anymore).
   */
  const openPlayer = () => {
    if (router.currentRoute.value.name !== "player") {
      router.push({ name: "player" });
    }
  };

  /**
   * Close the player. Pauses and immediately flushes the current playback
   * position (rather than waiting for the debounced save) since the player is
   * about to disappear, then navigates back to where the user opened it from.
   * Falls back to the playlist if there's no in-app back entry (there always
   * is in practice — the /player route's beforeEnter guard redirects any
   * cold/deep-link entry to the playlist before the player ever mounts).
   */
  const closePlayer = () => {
    isPlaying.value = false;
    saveToStorage();
    if (router.currentRoute.value.name === "player") {
      if (window.history.state?.back != null) {
        router.back();
      } else {
        router.push({ name: "playlist" });
      }
    }
  };

  /**
   * handleBeforeUnload - Global listener for tab close / page navigation.
   *
   * When the user closes the browser tab, refreshes the page, or navigates
   * to a different site, this handler fires and immediately persists the
   * current playback position to localStorage. Without this, the most
   * recent position could be lost because the debounced save in the
   * currentTime watcher might not have fired yet (it only writes every
   * 1 second). This ensures the user can always resume from where they
   * left off, even on an unexpected tab close.
   */
  const handleBeforeUnload = () => {
    saveToStorage();
  };

  // Register the beforeunload listener once when the singleton is created:
  // saves playback position before tab close/navigation. (Browser back is now
  // handled by the router — the player is the /player route — so the old
  // popstate interceptor is gone.)
  window.addEventListener("beforeunload", handleBeforeUnload);

  /**
   * Start playing the playlist queue from a specific index (Play All /
   * Play from here / queueAndPlay). Sets the episodes array, restores the
   * saved position for the starting episode, and begins playback — caching
   * the starting episode first (with the preparing overlay) when needed.
   *
   * @param {Array} episodeArray - The full array of episodes to play through
   * @param {number} [startIndex=0] - The index to start playing from
   */
  const playAllFromIndex = (episodeArray, startIndex = 0) => {
    if (episodeArray.length === 0) return;

    // Flush the current episode's position before replacing the playlist
    saveToStorage();

    // Set the playlist queue to the provided array
    episodes.value = episodeArray;

    // Clamp the start index to valid bounds
    currentEpisodeIndex.value = Math.min(startIndex, episodeArray.length - 1);

    // Open the dialog now — currentEpisode already reflects the new episode
    // (episodes/currentEpisodeIndex are set above), so whether it's cached
    // (plays immediately below) or not (the preparing overlay takes over),
    // the dialog is visible for both paths rather than only once cached.
    openPlayer();

    // Check if the starting episode is cached; if not, trigger caching and wait.
    // IMPORTANT: we check cached_at/progress_message from the episodeArray passed in,
    // not from the in-memory episodes.value. When called by queueAndPlay,
    // the episodeArray is the freshly-fetched /playlist response which may not have
    // progress_message set yet (the server hasn't processed the cache job). This is
    // why we always trigger caching here and rely on polling to detect if a download
    // is already underway on the server — the polling path handles the race correctly.
    const episode = episodes.value[currentEpisodeIndex.value];
    // Only TRULY cached episodes (cached_at set, not "downloading") play
    // immediately. `saved` is NOT a reliable cached signal — a saved episode can
    // have cached_at NULL with no file present, so it must still go through the
    // poll-until-cached path. cached_at is the authoritative "file exists" flag.
    const isCached = episode.cached_at && episode.cached_at !== "downloading";

    if (!isCached) {
      // Episode is not cached — trigger background caching and poll until ready,
      // then play. This enables the "smooth play flow" where hitting play starts
      // playback as soon as the episode is available.
      //
      // Note: the server's p-queue deduplication prevents a second download
      // job from being created if one is already underway — a duplicate
      // request gets isDuplicate=true and the existing job handles
      // completion. Polling below detects when that job finishes and
      // triggers playback.
      const ext = episode.episode_type === "audio" ? "mp3" : "mp4";
      const title = episode.title || episode.episode_title || "Episode";

      appSnackbar.value = { show: true, message: `Episode is being cached. Starting playback when ready...`, color: "info" };
      cachingEpisodeId.value = episode.id;
      cachingEpisodeTitle.value = title;
      cachingProgress.value = 0;
      cachingStatusMessage.value = `Caching: ${title}...`;

      // Trigger the cache download (fire-and-forget from the caller's
      // perspective, but triggerCache normalizes a give-up response — e.g.
      // the episode already hit maxDownloadFailures, a 200 OK with
      // success:false that a plain .catch() never saw — into a checkable
      // result). `pollInterval` is declared further down with `let` so this
      // callback (which resolves asynchronously, after that assignment has
      // run) can stop it immediately instead of the poll loop running for
      // up to its full 5-minute timeout waiting for a download that was
      // never going to start. Confirmed live on episode 262742.
      triggerCache(episode).then((result) => {
        if (!result.ok) {
          clearInterval(pollInterval);
          cachingEpisodeId.value = null;
          cachingProgress.value = 0;
          cachingStatusMessage.value = "";
          appSnackbar.value = { show: true, message: `"${title}": ${result.message}`, color: "error" };
        }
      });

      // Poll playlist status until the episode is cached (or actively downloading)
      // so we can start playback as soon as it's ready.
      const pollCacheStatus = async () => {
        try {
          const res = await api.get("/playlist/status");
          const statusData = res.data?.data || [];
          // Find this episode's status in the response
          const episodeStatus = statusData.find((s) => s.id === episode.id);
          if (episodeStatus) {
            // Surface live download progress in the player tab's caching overlay.
            if (typeof episodeStatus.progress === "number") {
              cachingProgress.value = episodeStatus.progress;
            }
            const nowCached = episodeStatus.cached_at && episodeStatus.cached_at !== "downloading";
            const nowDownloading = episodeStatus.cached_at === "downloading" ||
              episodeStatus.progress_message === "downloading";
            if (nowCached || nowDownloading) {
              // Update the episode in our local array with fresh status
              const ep = episodes.value[currentEpisodeIndex.value];
              ep.cached_at = episodeStatus.cached_at;
              ep.progress = episodeStatus.progress;
              ep.progress_message = episodeStatus.progress_message;
              ep.storage = episodeStatus.storage;
              // Play the episode (if now cached) or keep waiting (if still downloading)
              if (nowCached) {
                clearInterval(pollInterval);
                cachingEpisodeId.value = null;
                cachingProgress.value = 0;
                cachingStatusMessage.value = "";
                playEpisodeNow(ep);
              }
            }
          }
        } catch (err) {
          console.error("[playAllFromIndex] poll error:", err);
        }
      };

      // Start polling every 2 seconds, with a 5-minute timeout. `let` (not
      // `const`) so the triggerCache callback above — declared earlier in
      // this function but resolving later, asynchronously — can reference
      // and clear it once assigned.
      let elapsed = 0;
      let pollInterval = setInterval(() => {
        elapsed += 2000;
        if (elapsed >= 5 * 60 * 1000) {
          clearInterval(pollInterval);
          cachingEpisodeId.value = null;
          cachingStatusMessage.value = "";
          appSnackbar.value = { show: true, message: `Timed out waiting for cache: ${title}`, color: "error" };
        } else {
          pollCacheStatus();
        }
      }, 2000);

      // Also trigger one immediately after 500ms (cache might be fast)
      setTimeout(pollCacheStatus, 500);

      // Don't return early — the function continues but we'll start playback
      // in the poll callback once the episode is cached. To avoid playing
      // immediately in the wrong state, we return here.
      return;
    }

    // Episode is cached (or actively downloading) — play immediately in playlist mode
    playEpisodeNow(episode);
  };

  /**
   * Starts playback immediately for a given episode in playlist mode.
   * Sets the video URL, restores saved position, and opens the player.
   *
   * @param {Object} ep - The episode object to play (from the episodes array)
   */
  const playEpisodeNow = (ep) => {
    // Flush the current episode's position to localStorage before switching,
    // ensuring no progress is lost on the episode being left behind.
    saveToStorage();

    const episodeId = ep.id;
    const savedPosition = getEpisodePositionWithIntroSkip(episodeId);

    // Persist the restored position into episodePositions synchronously before
    // setting currentTime. The currentTime watcher (which also saves to
    // episodePositions) is a pre-flush watcher — it may not fire before the
    // currentEpisode watcher's async resolveVideoUrl completes and the video
    // element fires loadedmetadata. Saving here guarantees the position is
    // present when onLoadedMetadata calls getEpisodePosition.
    episodePositions.value[episodeId] = savedPosition;
    currentTime.value = savedPosition;

    const mode = "play";
    ep.video_url = ep.episode_type === "audio"
      ? `${import.meta.env.VITE_API_BASE_URL}/${mode}/${ep.video_id}.mp3`
      : `${import.meta.env.VITE_API_BASE_URL}/${mode}/${ep.video_id}.mp4`;

    resetSkipFlags();
    isPlaying.value = true;
    openPlayer();
  };

  /**
   * Advance to the next episode in the playlist.
   * Increments the index, restores saved position, builds the media URL,
   * and starts playback. No-ops when already at the last episode.
   * Used by the manual next button — a manual skip deliberately does NOT
   * remove the skipped episode from the queue (see finishAndAdvance for
   * the completion path, which does).
   */
  const playNext = () => {
    // Check if there is a next episode
    if (currentEpisodeIndex.value < episodes.value.length - 1) {
      // Flush the current episode's position before navigating away
      saveToStorage();
      currentEpisodeIndex.value++;
      const nextEpisodeId = episodes.value[currentEpisodeIndex.value].id;

      // Restore saved position for the next episode.
      // Save directly into episodePositions to guarantee it's present when
      // onLoadedMetadata fires — avoids a race with the currentTime watcher.
      const savedPosition = getEpisodePositionWithIntroSkip(nextEpisodeId);
      episodePositions.value[nextEpisodeId] = savedPosition;
      currentTime.value = savedPosition;

      resetSkipFlags();
      isPlaying.value = true; // Triggers auto-play in MediaPlayer component

      // Build the media URL for the next episode
      const mode = "play";
      if (currentEpisode.value) {
        currentEpisode.value.video_url =
          currentEpisode.value.episode_type === "audio"
            ? `${import.meta.env.VITE_API_BASE_URL}/${mode}/${currentEpisode.value.video_id}.mp3`
            : `${import.meta.env.VITE_API_BASE_URL}/${mode}/${currentEpisode.value.video_id}.mp4`;
      }
    }
  };

  /**
   * Go back to the previous episode in the playlist.
   * Decrements the index, restores saved position, builds the media URL,
   * and starts playback. No-ops when already at the first episode.
   */
  const playPrevious = () => {
    // Check if there is a previous episode
    if (currentEpisodeIndex.value > 0) {
      // Flush the current episode's position before navigating away
      saveToStorage();
      currentEpisodeIndex.value--;
      const prevEpisodeId = episodes.value[currentEpisodeIndex.value].id;

      // Restore saved position for the previous episode.
      // Save directly into episodePositions to guarantee it's present when
      // onLoadedMetadata fires — avoids a race with the currentTime watcher.
      const savedPosition = getEpisodePositionWithIntroSkip(prevEpisodeId);
      episodePositions.value[prevEpisodeId] = savedPosition;
      currentTime.value = savedPosition;

      resetSkipFlags();
      isPlaying.value = true; // Triggers auto-play in MediaPlayer component

      // Build the media URL for the previous episode
      const mode = "play";
      if (currentEpisode.value) {
        currentEpisode.value.video_url =
          currentEpisode.value.episode_type === "audio"
            ? `${import.meta.env.VITE_API_BASE_URL}/${mode}/${currentEpisode.value.video_id}.mp3`
            : `${import.meta.env.VITE_API_BASE_URL}/${mode}/${currentEpisode.value.video_id}.mp4`;
      }
    }
  };

  /**
   * finishAndAdvance - Central "an episode finished playing" handler.
   * Called when playback of the current episode COMPLETES (natural end or
   * outro skip) — NOT on a manual next-button skip, which deliberately
   * keeps the episode in the queue.
   *
   * Queue semantics: when autoRemovePlayed is enabled, the finished episode
   * leaves the playlist (like Overcast/Pocket Casts queues). The exact order
   * below is index-safety-critical:
   *   1. Capture the finished episode AND the next episode BY REFERENCE
   *      before any mutation.
   *   2. Reset the finished episode's saved position (fully played).
   *   3. Splice the finished episode out LOCALLY first (instant UI), then
   *      fire-and-forget the server delete — on failure the episode simply
   *      reappears at the next playlist fetch, and we show an error snackbar.
   *   4. Advance BY ID, not by index: after the splice the next episode has
   *      shifted into the finished episode's slot; playEpisode's findIndex
   *      locates it correctly whether or not the splice happened.
   *
   * @param {Object} [options]
   * @param {boolean} [options.advance=true] - false when the sleep timer
   *   (End of Episode) stopped playback: remove-if-enabled but don't play on
   */
  const finishAndAdvance = ({ advance = true } = {}) => {
    const finishedIndex = currentEpisodeIndex.value;
    const finished = episodes.value[finishedIndex];
    if (!finished) return;

    // Capture the next episode BEFORE any mutation (see docstring).
    const next = episodes.value[finishedIndex + 1] || null;

    // The episode was fully played — clear its resume position.
    resetEpisodePosition(finished.id);

    // Queue semantics: a finished episode leaves the playlist (setting-gated).
    let removed = false;
    if (autoRemovePlayed.value) {
      episodes.value.splice(finishedIndex, 1);
      removed = true;
      // Fire-and-forget server delete. Deliberately NOT the removeEpisode()
      // helper: that awaits and shows a success snackbar per episode, which
      // would be noisy on every auto-advance.
      api.delete(`/playlist/episode/${finished.id}`).catch((error) => {
        console.error("Failed to remove played episode from playlist:", error);
        showSnackbar(
          `Could not remove "${finished.title || finished.episode_title || "episode"}" from the playlist`,
          "error",
        );
      });
    }

    if (advance && next) {
      // Continue down the queue (id-based lookup — splice-proof).
      playEpisode(next.id);
      return;
    }

    // Queue exhausted, or the sleep timer stopped playback.
    isPlaying.value = false;
    if (removed) {
      // The episode the player was showing no longer exists in the queue —
      // close the player rather than display a stale/ended item.
      closePlayer();
    }
  };

  /**
   * Manually set the saved position for an episode.
   *
   * @param {number|string} episodeId - The episode ID
   * @param {number} position - Playback position in seconds
   */
  const setEpisodePosition = (episodeId, position) => {
    episodePositions.value[episodeId] = position;
  };

  /**
   * Reset the saved position for an episode back to 0.
   * Also resets currentTime if the episode is currently playing.
   *
   * @param {number|string} episodeId - The episode ID to reset
   */
  const resetEpisodePosition = (episodeId) => {
    if (episodePositions.value[episodeId]) {
      delete episodePositions.value[episodeId];
    }
    // If this episode is currently playing, reset the playback position too
    if (currentEpisode.value && currentEpisode.value.id === episodeId) {
      currentTime.value = 0;
    }
  };

  /**
   * Toggle between playing and paused states.
   */
  const togglePlay = () => {
    isPlaying.value = !isPlaying.value;
  };

  /**
   * Seek to a specific time in the current episode.
   * Clamps the time to valid bounds (0 to duration) and
   * immediately saves the position.
   *
   * @param {number} time - Target time in seconds
   */
  const seek = (time) => {
    const newTime = Math.max(0, Math.min(time, duration.value));
    currentTime.value = newTime;

    // Save position immediately when seeking (don't wait for debounce)
    if (currentEpisode.value) {
      episodePositions.value[currentEpisode.value.id] = newTime;
    }
  };

  /**
   * Set the audio volume, clamped between 0 and 1.
   *
   * @param {number} newVolume - Volume level (0 to 1)
   */
  const setVolume = (newVolume) => {
    volume.value = Math.max(0, Math.min(1, newVolume));
  };

  /**
   * Set the playback speed multiplier.
   *
   * @param {number} rate - Playback rate (e.g., 0.5, 1, 1.5, 2)
   */
  const setPlaybackRate = (rate) => {
    playbackRate.value = rate;
  };

  // ── Cleanup and reset methods ───────────────────────────────────

  /**
   * Clear all media player data from localStorage and reset all state.
   * Called during logout to ensure no user data persists.
   */
  const clearAllData = () => {
    // List of all localStorage keys used by the media player
    const mediaPlayerKeys = [
      "mediaPlayerEpisodePositions",
      "mediaPlayerSleepTimer",
      "mediaPlayerVolume",
      "mediaPlayerPlaybackRate",
      "mediaPlayerSkipIntro",
      "mediaPlayerSkipOutro",
      "mediaPlayerAutoRemovePlayed",
      "mediaPlayerMobileMode"
    ];

    // Remove each key from localStorage
    mediaPlayerKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    // Reset all reactive state to defaults
    episodes.value = [];
    currentEpisodeIndex.value = 0;
    currentTime.value = 0;
    duration.value = 0;
    isPlaying.value = false;
    episodePositions.value = {};

    // Reset preferences to factory defaults
    volume.value = 1;
    playbackRate.value = 1;
    sleepTimerDuration.value = -1;
    sleepTimerActive.value = false;
    skipIntroDuration.value = 0;
    skipOutroDuration.value = 0;
    autoRemovePlayed.value = true;
  };

  /**
   * Clean up timers, intervals, and global event listeners.
   * Should be called when the app is shutting down to prevent memory leaks.
   */
  const cleanup = () => {
    clearSleepTimer();
    stopCountdown();
    clearTimeout(savePositionsTimeout);
    // Remove the global listener registered by the singleton
    window.removeEventListener("beforeunload", handleBeforeUnload);
  };

  /**
   * Format a time in seconds to a "M:SS" display string.
   *
   * @param {number} seconds - Time in seconds
   * @returns {string} Formatted time string (e.g., "3:07")
   */
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // ── Build and cache the singleton instance ──────────────────────
  // Assemble all refs, computed properties, and methods into one object.
  // This object is cached in mediaPlayerInstance so subsequent calls to
  // useMediaPlayer() return exactly the same references, ensuring all
  // consumers share identical reactive state.

  mediaPlayerInstance = {
    // ── Reactive state refs ──────────────────────────────────────
    episodes,
    currentEpisodeIndex,
    currentTime,
    duration,
    isPlaying,
    volume,
    playbackRate,
    sleepTimerDuration,
    sleepTimerActive,
    sleepTimerEndTime,
    sleepTimerRemainingSeconds,
    skipIntroDuration,
    skipOutroDuration,
    skipIntroEnabled,
    skipOutroEnabled,
    autoRemovePlayed,

    introSkipped,
    outroSkipped,
    episodePositions,
    showPlayer,
    loading,
    total_playlist,
    snackbar,

    // ── Computed (read-only derived state) ─────────────────────
    currentEpisode,
    progress,
    sleepTimerFormatted,
    shouldSkipIntro,
    shouldSkipOutro,
    hasNextEpisode,
    hasPreviousEpisode,
    playlistPositionText,

    // ── Action methods ─────────────────────────────────────────
    addEpisodes,
    setEpisodes,
    playEpisode,
    queueAndPlay,
    cacheEpisodeWithProgress,
    openPlayer,
    closePlayer,
    playAllFromIndex,
    playNext,
    playPrevious,
    finishAndAdvance,
    getEpisodePosition,
    getEpisodePositionWithIntroSkip,
    setEpisodePosition,
    resetEpisodePosition,
    togglePlay,
    seek,
    setVolume,
    setPlaybackRate,
    setSleepTimer,
    clearSleepTimer,
    checkSleepTimerOnEnd,
    skipIntro,
    skipOutro,
    resetSkipFlags,
    getPlaylist,
    removeEpisode,
    showSnackbar,
    formatTime,
    cleanup,

    // ── Storage persistence methods ─────────────────────────────
    saveToStorage,
    loadFromStorage,

    // ── Cleanup / reset methods ─────────────────────────────────
    clearAllData,
  };

  return mediaPlayerInstance;
}
